/**
 * WB4 Phase 3 — Palette intent → proposal command (no auto-execute).
 */

import type { WorkbenchCommandContext, ResolvedCommand } from './command-resolver';
import {
  resolveKernelActionPaletteGroup,
  buildExplainPaletteHandlerRef,
} from './workbench-command-palette-contract';
import { resolveAiSuggestedActionsFromIds } from './ai-suggested-action-resolver';
import { resolvePaletteLlmCommands } from './workbench-palette-llm-resolver';
import type { PaletteIntentResolution } from './workbench-palette-intent';
import { resolvePaletteIntentFromNaturalLanguage } from './workbench-palette-intent';
import { resolveActionRiskTier } from './workbench-action-safety';
import type { WorkbenchRiskTier } from './workbench-action-safety-policy';
import { omitUndefined } from '@shared/utils/omitUndefined';
import { resolveRegistryActionsForPaletteContext } from './workbench-palette-intent-registry';

export const PALETTE_INTENT_PROPOSAL_COMMAND_ID_PREFIX = 'palette-intent-proposal' as const;

export interface PaletteIntentProposal {
  intent: PaletteIntentResolution['intent'];
  label: string;
  riskTier: WorkbenchRiskTier;
  interpretationSummary: string;
  missingFields: readonly { key: string; label: string }[];
  payload: Partial<Record<string, string>>;
  source: PaletteIntentResolution['source'];
  /** Resolved palette command — requires explicit user approval before execution. */
  command?: ResolvedCommand;
  requiresClarification: boolean;
}

function isExplainOnlyIntent(intent: PaletteIntentResolution['intent']): boolean {
  return intent === 'invoice_payment_status_explain' || intent === 'blocked_action_reason_explain';
}

function buildExplainProposalCommand(resolution: PaletteIntentResolution): ResolvedCommand {
  const explainSourceId = resolution.intent.replace(/_/g, '-');
  return {
    id: `${PALETTE_INTENT_PROPOSAL_COMMAND_ID_PREFIX}:${resolution.intent}`,
    label: resolution.spec.label,
    section: 'action',
    kind: 'action',
    group: 'explain',
    handlerRef: buildExplainPaletteHandlerRef(explainSourceId),
    priority: 108,
    reason: resolution.interpretationSummary,
  };
}

function resolveProposalCommand(
  resolution: PaletteIntentResolution,
  ctx: WorkbenchCommandContext
): ResolvedCommand | undefined {
  if (isExplainOnlyIntent(resolution.intent)) {
    return buildExplainProposalCommand(resolution);
  }

  const registryActions = resolveRegistryActionsForPaletteContext(ctx);
  const { resolved } = resolveAiSuggestedActionsFromIds(
    [resolution.spec.actionId],
    registryActions
  );
  const action = resolved[0];
  if (!action) {
    const llmFallback = resolvePaletteLlmCommands(
      { suggestedActionIds: [resolution.spec.actionId], stub: true },
      ctx
    );
    return llmFallback[0];
  }

  return {
    id: `${PALETTE_INTENT_PROPOSAL_COMMAND_ID_PREFIX}:${resolution.intent}`,
    label: action.label,
    section: 'action',
    kind: 'action',
    group: resolveKernelActionPaletteGroup(action),
    handlerRef: action.handlerRef,
    priority: 96,
    ...(action.availability === 'disabled' || action.forbidden ? { disabled: true } : {}),
    reason: action.reason?.trim() || resolution.interpretationSummary,
  };
}

export function rebuildPaletteIntentProposal(
  proposal: PaletteIntentProposal,
  clarification: Partial<Record<string, string>>,
  ctx: WorkbenchCommandContext,
  query: string
): PaletteIntentProposal {
  const resolution = resolvePaletteIntentFromNaturalLanguage({
    query,
    clarification,
    ...(ctx.businessDomain !== undefined ? { businessDomain: ctx.businessDomain } : {}),
    ...(ctx.object?.id !== undefined ? { objectId: ctx.object.id } : {}),
    ...(proposal.payload.customerId ? { customerId: proposal.payload.customerId } : {}),
    ...(proposal.payload.orderId ? { orderId: proposal.payload.orderId } : {}),
    ...(proposal.payload.invoiceId ? { invoiceId: proposal.payload.invoiceId } : {}),
    ...(proposal.payload.blockedActionId
      ? { blockedActionId: proposal.payload.blockedActionId }
      : {}),
  });

  if (!resolution || resolution.intent !== proposal.intent) {
    return proposal;
  }

  return buildPaletteIntentProposal(resolution, ctx);
}

export function buildPaletteIntentProposal(
  resolution: PaletteIntentResolution,
  ctx: WorkbenchCommandContext
): PaletteIntentProposal {
  const missingFields = resolution.missingFieldKeys.map((key) => {
    const field = resolution.spec.requiredFields.find((entry) => entry.key === key);
    return { key, label: field?.label ?? key };
  });

  const requiresClarification = !isExplainOnlyIntent(resolution.intent) && missingFields.length > 0;

  const riskPolicy = resolveActionRiskTier(
    omitUndefined({
      actionId: resolution.spec.actionId,
      businessDomain: ctx.businessDomain,
    })
  );

  const command = requiresClarification ? undefined : resolveProposalCommand(resolution, ctx);

  return {
    intent: resolution.intent,
    label: resolution.spec.label,
    riskTier: resolution.spec.riskTier ?? riskPolicy.riskTier,
    interpretationSummary: resolution.interpretationSummary,
    missingFields,
    payload: resolution.payload,
    source: resolution.source,
    ...(command !== undefined ? { command } : {}),
    requiresClarification,
  };
}
